import { isHttpsUrl } from "./apiEmptyList";
import { isAllowedAssetUrl } from "./adDisplayRules";

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Caps aligned with DB NUMERIC/DECIMAL and the sizes we want the API to enforce. */
export const ADS_LIMITS = Object.freeze({
    NAME: 100,
    PERSON: 100,
    EMAIL: 254,
    URL: 2048,
    DESCRIPTION: 2000,
    NOTES: 2000,
    TITLE: 200,
    AUDIENCE: 200,
    TRANSACTION_ID: 100,
    MONEY_MAX: 9999999999.99,
    DAILY_MONEY_MAX: 99999999.99,
});

const formatMoneyCap = (max) => max.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const lengthOf = (value) => String(value ?? "").trim().length;

const rejectIfTooLong = (fields, key, value, max, label) => {
    if (value && lengthOf(value) > max) {
        fields[key] = `${label} must be ${max} characters or fewer.`;
    }
};

const decimalPlaces = (value) => {
    const text = String(value);
    const dot = text.indexOf(".");
    return dot === -1 ? 0 : text.length - dot - 1;
};

const rejectIfMoney = (fields, key, value, { required = false, min = 0, max = ADS_LIMITS.MONEY_MAX, emptyMessage, rangeMessage } = {}) => {
    if (value === "" || value == null) {
        if (required) {
            fields[key] = emptyMessage || "Amount must be greater than zero.";
        }
        return;
    }
    const amount = Number(value);
    if (!Number.isFinite(amount)) {
        fields[key] = "Enter a valid amount.";
        return;
    }
    if (decimalPlaces(value) > 2) {
        fields[key] = "Use up to 2 decimal places.";
        return;
    }
    if (amount < min || amount > max) {
        fields[key] = rangeMessage || (min > 0
            ? `Amount must be greater than zero and at most ${formatMoneyCap(max)}.`
            : `Amount cannot be negative or exceed ${formatMoneyCap(max)}.`);
    }
};

const pack = (fields, message) => {
    const keys = Object.keys(fields);
    return {
        ok: keys.length === 0,
        fields,
        message: keys.length ? message : "",
    };
};

export const toApiDateTime = (value) => {
    if (!value) {
        return null;
    }
    return value.length === 16 ? `${value}:00` : value;
};

export const toLocalDateTime = (value) => {
    if (!value) {
        return "";
    }
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value)) {
        return value;
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
        return String(value).slice(0, 16);
    }
    const pad = (part) => String(part).padStart(2, "0");
    return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}T${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`;
};

export const SAVE_FAILED = "Could not save that. Please try again.";
export const UPLOAD_FAILED = "Could not upload that image. Please try again.";

const TECHNICAL_ERROR = /badsqlgrammar|sqlgrammar|psql|jdbc|postgresql|deleted_at|deleted_by|not a member of this club|switch club and retry|could not extract resultset|bad sql/i;

export const isTechnicalAdsError = (message = "", status) => {
    if (status === 403) {
        return true;
    }
    return TECHNICAL_ERROR.test(String(message || ""));
};

const apiMessage = (err) => err?.response?.data?.Message || "";

export const adsApiMessage = (err) => {
    const api = apiMessage(err);
    if (isTechnicalAdsError(api, err?.response?.status) || !api) {
        return SAVE_FAILED;
    }
    return api;
};

export const clubUploadMessage = (err) => {
    const api = apiMessage(err);
    if (isTechnicalAdsError(api, err?.response?.status) || !api) {
        return UPLOAD_FAILED;
    }
    return api;
};

export const validateSponsorForm = (form = {}) => {
    const fields = {};
    if (!form.name?.trim()) {
        fields.name = "Enter a sponsor name.";
    }
    rejectIfTooLong(fields, "name", form.name, ADS_LIMITS.NAME, "Sponsor name");
    rejectIfTooLong(fields, "contactPerson", form.contactPerson, ADS_LIMITS.PERSON, "Contact person");
    rejectIfTooLong(fields, "contactEmail", form.contactEmail, ADS_LIMITS.EMAIL, "Email");
    rejectIfTooLong(fields, "logoUrl", form.logoUrl, ADS_LIMITS.URL, "Logo URL");
    rejectIfTooLong(fields, "companyWebsite", form.companyWebsite, ADS_LIMITS.URL, "Website");
    rejectIfTooLong(fields, "description", form.description, ADS_LIMITS.DESCRIPTION, "Description");
    if (form.contactEmail && !fields.contactEmail && !emailPattern.test(form.contactEmail.trim())) {
        fields.contactEmail = "Enter a valid email.";
    }
    if (form.logoUrl && !fields.logoUrl && !isAllowedAssetUrl(form.logoUrl)) {
        fields.logoUrl = "Use an https URL or an uploaded creative.";
    }
    if (form.companyWebsite && !fields.companyWebsite && !isHttpsUrl(form.companyWebsite)) {
        fields.companyWebsite = "Use an https website.";
    }
    return pack(fields, "Fix the highlighted sponsor fields.");
};

export const validateDealForm = (form = {}) => {
    const fields = {};
    if (!form.sponsorId) {
        fields.sponsorId = "Choose a sponsor.";
    }
    if (!form.targetId) {
        fields.targetId = "Choose what this deal attaches to.";
    }
    rejectIfMoney(fields, "amount", form.amount, { min: 0, max: ADS_LIMITS.MONEY_MAX });
    rejectIfTooLong(fields, "contractUrl", form.contractUrl, ADS_LIMITS.URL, "Contract URL");
    if (form.startDate && form.endDate && form.endDate < form.startDate) {
        fields.endDate = "End date must be on or after the start date.";
    }
    if (form.contractUrl && !fields.contractUrl && !isHttpsUrl(form.contractUrl)) {
        fields.contractUrl = "Use an https contract link.";
    }
    return pack(fields, "Fix the highlighted deal fields.");
};

export const validatePaymentForm = (form = {}) => {
    const fields = {};
    if (!form.sponsorshipId) {
        fields.sponsorshipId = "Choose a deal.";
    }
    if (!form.paymentDate) {
        fields.paymentDate = "Choose a payment date.";
    }
    rejectIfMoney(fields, "amount", form.amount, { required: true, min: 0.01, max: ADS_LIMITS.MONEY_MAX });
    rejectIfTooLong(fields, "receiptUrl", form.receiptUrl, ADS_LIMITS.URL, "Receipt URL");
    rejectIfTooLong(fields, "transactionId", form.transactionId, ADS_LIMITS.TRANSACTION_ID, "Transaction ID");
    rejectIfTooLong(fields, "notes", form.notes, ADS_LIMITS.NOTES, "Notes");
    if (form.receiptUrl && !fields.receiptUrl && !isHttpsUrl(form.receiptUrl)) {
        fields.receiptUrl = "Use an https receipt link.";
    }
    return pack(fields, "Fix the highlighted payment fields.");
};

export const validateBannerForm = (form = {}, { placementAllowed = true } = {}) => {
    const fields = {};
    if (!form.title?.trim()) {
        fields.title = "Enter a banner title.";
    }
    rejectIfTooLong(fields, "title", form.title, ADS_LIMITS.TITLE, "Title");
    rejectIfTooLong(fields, "imageUrl", form.imageUrl, ADS_LIMITS.URL, "Image URL");
    rejectIfTooLong(fields, "targetUrl", form.targetUrl, ADS_LIMITS.URL, "Link");
    rejectIfTooLong(fields, "videoUrl", form.videoUrl, ADS_LIMITS.URL, "Video URL");
    rejectIfTooLong(fields, "targetAudience", form.targetAudience, ADS_LIMITS.AUDIENCE, "Target audience");
    if (form.dailyBudget !== "" && form.dailyBudget != null) {
        rejectIfMoney(fields, "dailyBudget", form.dailyBudget, { min: 0.01, max: ADS_LIMITS.DAILY_MONEY_MAX });
    }
    if (form.priority !== "" && form.priority != null) {
        const priority = Number(form.priority);
        if (!Number.isFinite(priority) || priority < 0 || priority > 100) {
            fields.priority = "Priority must be between 0 and 100.";
        }
    }
    if (!form.imageUrl) {
        fields.imageUrl = "Upload an image first.";
    } else if (!fields.imageUrl && !isAllowedAssetUrl(form.imageUrl)) {
        fields.imageUrl = "Use an uploaded creative or an https image.";
    }
    if (form.targetUrl && !fields.targetUrl && !isHttpsUrl(form.targetUrl)) {
        fields.targetUrl = "Use an https link.";
    }
    if (form.adType === "VIDEO" && !form.videoUrl) {
        fields.videoUrl = "Video URL is required for video ads.";
    }
    if (form.videoUrl && !fields.videoUrl && !isHttpsUrl(form.videoUrl) && !isAllowedAssetUrl(form.videoUrl)) {
        fields.videoUrl = "Use an https video link.";
    }
    if (!placementAllowed) {
        fields.placement = "That placement is not allowed for this sponsorship tier.";
    }
    return pack(fields, "Fix the highlighted banner fields.");
};

export const validateCampaignForm = (form = {}) => {
    const fields = {};
    if (!form.name?.trim()) {
        fields.name = "Enter a campaign name.";
    }
    rejectIfTooLong(fields, "name", form.name, ADS_LIMITS.NAME, "Campaign name");
    rejectIfTooLong(fields, "description", form.description, ADS_LIMITS.DESCRIPTION, "Description");
    rejectIfTooLong(fields, "targetAudience", form.targetAudience, ADS_LIMITS.AUDIENCE, "Target audience");
    if (!form.startDate) {
        fields.startDate = "Choose a start date.";
    }
    if (!form.endDate) {
        fields.endDate = "Choose an end date.";
    }
    if (form.startDate && form.endDate && form.endDate < form.startDate) {
        fields.endDate = "End must be after start.";
    }
    rejectIfMoney(fields, "totalBudget", form.totalBudget, {
        required: true,
        min: 0.01,
        max: ADS_LIMITS.MONEY_MAX,
        emptyMessage: "Total budget must be greater than zero.",
        rangeMessage: `Total budget must be greater than zero and at most ${formatMoneyCap(ADS_LIMITS.MONEY_MAX)}.`,
    });
    if (form.dailyBudget !== "" && form.dailyBudget != null) {
        rejectIfMoney(fields, "dailyBudget", form.dailyBudget, {
            min: 0.01,
            max: ADS_LIMITS.DAILY_MONEY_MAX,
            rangeMessage: `Daily budget must be greater than zero and at most ${formatMoneyCap(ADS_LIMITS.DAILY_MONEY_MAX)}.`,
        });
        const total = Number(form.totalBudget);
        const daily = Number(form.dailyBudget);
        if (!fields.dailyBudget && Number.isFinite(daily) && Number.isFinite(total) && daily > total) {
            fields.dailyBudget = "Daily budget cannot exceed the total budget.";
        }
    }
    return pack(fields, "Fix the highlighted campaign fields.");
};

export const campaignPayload = (form, clubId) => ({
    name: form.name.trim(),
    description: form.description || null,
    startDate: toApiDateTime(form.startDate),
    endDate: toApiDateTime(form.endDate),
    totalBudget: Number(form.totalBudget),
    dailyBudget: form.dailyBudget ? Number(form.dailyBudget) : null,
    status: form.status || "DRAFT",
    targetAudience: form.targetAudience || null,
    clubId,
});
